import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import type { DxDiagnosisResultCsvExportRequest } from '@src/api/tenant/dxDiagnosis/dxDiagnosisResultApiClient';
import { dxDiagnosisResultApiClient } from '@src/api/tenant/dxDiagnosis/dxDiagnosisResultApiClient';
import { DxDiagnosisResult } from '@src/api/types/dxDiagnosisResult';
import { Pagination } from '@src/api/types/pagination';
import SvgIconDownload from '@src/assets/images/common/icon/chat-action/SvgIconDownload';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import {
  DxDiagnosisResultStatus,
  getDxDiagnosisResultStatusLabel,
} from '@src/enum/dxDiagnosisResultStatus';
import { useToast } from '@src/features/toast/context/toastContext';
import Button from '@src/ui/elements/button/Button';
import DropdownCheckbox from '@src/ui/elements/form/DropdownCheckbox';
import InputSearch from '@src/ui/elements/form/InputSearch';
import Th from '@src/ui/elements/table/Th';
import Title from '@src/ui/elements/typography/Title';
import logger from '@src/utils/logger';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DxDiagnosisListForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [diagnoses, setDiagnoses] = useState<DxDiagnosisResult[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false); // 初期値をfalseに設定
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [keyword, setKeyword] = useState<string>('');
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
    DxDiagnosisResultStatus.IN_PROGRESS.toString(),
    DxDiagnosisResultStatus.COMPLETED.toString(),
    DxDiagnosisResultStatus.DELETED.toString(),
  ]);
  const [isExporting, setIsExporting] = useState(false);

  // ステータスプルダウンのオプションを生成
  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'すべて' },
      {
        value: DxDiagnosisResultStatus.IN_PROGRESS.toString(),
        label: getDxDiagnosisResultStatusLabel(DxDiagnosisResultStatus.IN_PROGRESS),
      },
      {
        value: DxDiagnosisResultStatus.COMPLETED.toString(),
        label: getDxDiagnosisResultStatusLabel(DxDiagnosisResultStatus.COMPLETED),
      },
      {
        value: DxDiagnosisResultStatus.DELETED.toString(),
        label: getDxDiagnosisResultStatusLabel(DxDiagnosisResultStatus.DELETED),
      },
    ],
    [],
  );

  // プルダウンの初期選択状態を計算
  const initialSelectedStatusOptions = useMemo(() => {
    if (selectedStatuses.length === 0) {
      return [];
    }
    if (selectedStatuses.length === statusOptions.length - 1) {
      return statusOptions;
    }
    return statusOptions.filter(
      (option) => option.value !== 'all' && selectedStatuses.includes(option.value),
    );
  }, [selectedStatuses, statusOptions]);

  // 診断一覧データをAPIから取得
  const loadDiagnoses = async (
    page = 1,
    sortField: string | null = null,
    sortDirection: 'asc' | 'desc' = 'asc',
    keyword: string | null = null,
    statusFilter: number[] | null = null,
  ) => {
    const params: {
      page: number;
      sort?: string;
      direction?: 'asc' | 'desc';
      keyword?: string;
      status?: number[];
    } = {
      page,
    };
    if (sortField) {
      params.sort = sortField;
    }
    if (sortDirection) {
      params.direction = sortDirection;
    }
    if (keyword) {
      params.keyword = keyword;
    }
    if (statusFilter && statusFilter.length > 0) {
      params.status = statusFilter;
    }

    setLoading(true);

    const res = await dxDiagnosisResultApiClient.index(params);
    handleApiResponse(res, (data) => {
      setDiagnoses(data.data);
      setPagination(data.pagination);
      setFilteredCount(data.pagination?.total || 0);
      setSearchedKeyword(keyword || '');
    });

    setLoading(false);
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const page = parseInt(query.get('page') || '1', 10);
    const sort = query.get('sort') || 'id';
    const direction = (query.get('direction') as 'asc' | 'desc') || 'desc';
    const keyword = query.get('keyword') || '';
    const statusParam = query.get('status');
    const statusFilter = statusParam ? statusParam.split(',').map((s) => parseInt(s)) : null;

    setSortField(sort);
    setSortDirection(direction || 'asc');
    setKeyword(keyword);
    setSearchedKeyword(keyword);
    if (statusParam) {
      setSelectedStatuses(statusParam.split(','));
    }

    loadDiagnoses(page, sort, direction || 'asc', keyword, statusFilter);
  }, [location.search]);

  // ページ変更時のURL更新
  const handlePageChange = (page: number) => {
    const currentPage = new URLSearchParams(location.search).get('page');
    if (currentPage === page.toString()) {
      return;
    }
    const query = new URLSearchParams(location.search);
    query.set('page', page.toString());
    navigate({ search: query.toString() });
  };

  // ソート変更時のURL更新
  const handleSortChange = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    const query = new URLSearchParams(location.search);
    query.set('sort', field);
    query.set('direction', newDirection);
    query.set('page', '1'); // ソート変更時はページを1にリセット
    navigate({ search: query.toString() });
  };

  // 検索キーワードの入力値更新
  const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  // 検索実行時のURL更新
  const handleKeywordSearch = () => {
    const query = new URLSearchParams(location.search);
    const currentKeyword = query.get('keyword');
    if (currentKeyword === keyword.trim()) {
      return;
    }

    setSearchedKeyword(keyword);
    setFilteredCount(null);
    query.set('keyword', keyword);
    query.set('page', '1');
    navigate({ search: query.toString() });
  };

  // Enterキーで検索実行
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleKeywordSearch();
    }
  };

  // ステータス文字列を数値配列に変換
  const convertStatusToNumbers = (statuses: string[]) =>
    statuses.filter((status) => status !== 'all').map((status) => parseInt(status));

  // 全ステータスが選択されているか判定
  const isAllStatusesSelected = (statuses: string[]) =>
    statuses.length === 0 || statuses.length === statusOptions.length - 1;

  // ステータスプルダウン変更時の処理
  const handleSelectStatus = (selectedOptions: { value: string; label: React.ReactNode }[]) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    const statusCodes = selectedValues.filter((value) => value !== 'all');
    setSelectedStatuses(statusCodes);

    // URLパラメータを更新して絞り込み検索を実行
    const query = new URLSearchParams(location.search);
    if (isAllStatusesSelected(statusCodes)) {
      query.delete('status');
    } else {
      query.set('status', statusCodes.join(','));
    }
    query.set('page', '1');
    navigate({ search: query.toString() });
  };

  // CSVエクスポート処理
  const handleCsvExport = async () => {
    setIsExporting(true);

    try {
      const params: DxDiagnosisResultCsvExportRequest = {};

      const statusNumbers = convertStatusToNumbers(selectedStatuses);
      if (!isAllStatusesSelected(selectedStatuses)) {
        params.status = statusNumbers;
      }

      if (searchedKeyword) {
        params.keyword = searchedKeyword;
      }

      if (sortField) {
        params.sort = sortField;
        params.direction = sortDirection;
      }

      const response = await dxDiagnosisResultApiClient.exportCsv(params);

      handleApiResponse<Blob, never>(
        response,
        (csvBlob) => {
          const bom = '\uFEFF';
          const blobWithBom = new Blob([bom, csvBlob], { type: 'text/csv;charset=utf-8;' });

          const blobUrl = window.URL.createObjectURL(blobWithBom);
          const anchorElement = document.createElement('a');
          anchorElement.href = blobUrl;
          const fileNamePrefix = 'DX診断結果一覧';
          const currentData = dayjs().format('YYYYMMDD_HHmmss');
          anchorElement.download = `${fileNamePrefix}_${currentData}.csv`;
          document.body.appendChild(anchorElement);
          anchorElement.click();
          document.body.removeChild(anchorElement);
          window.URL.revokeObjectURL(blobUrl);
        },
        (error) => {
          logger.error('CSVのエクスポートに失敗しました。:', error);
          toast('CSVのエクスポートに失敗しました。');
        },
      );
    } catch (error) {
      logger.error('CSVのエクスポートに失敗しました。:', error);
      toast('CSVのエクスポートに失敗しました。');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main>
      <SpinnerOverlay loading={loading} />
      <div>
        <div className='flex items-center justify-between'>
          <Title>DX診断結果一覧</Title>
          <div className='flex items-center gap-4'>
            <div className='w-[250px]'>
              <InputSearch
                key={searchedKeyword}
                value={keyword}
                defaultValue={searchedKeyword}
                onChange={handleKeywordChange}
                onKeyDown={handleKeyDown}
                onSubmit={handleKeywordSearch}
                onClear={() => {
                  setKeyword('');
                  navigate({ search: '' });
                }}
              />
            </div>
          </div>
        </div>

        <div className='mt-[10px] flex items-center justify-end'>
          <div className='flex items-center gap-3'>
            <DropdownCheckbox
              className='!w-[190px] !py-3'
              dropdownWidth={190}
              options={statusOptions}
              onSelect={handleSelectStatus}
              allSelectedText='すべて'
              selectedCountText={(count) => `${count}件のステータス`}
              placeholder='ステータス'
              initialSelectedOptions={initialSelectedStatusOptions}
            />
            <Button
              size={isExporting ? 'l' : 's'}
              className='!px-3 !py-2'
              onClick={handleCsvExport}
              loading={isExporting}
            >
              <SvgIconDownload />
            </Button>
          </div>
        </div>

        <div className='mt-10'></div>
        {searchedKeyword && (
          <p className='overflow-wrap-anywhere mb-4'>
            「{searchedKeyword}」で絞り込んだ結果：{filteredCount}件
          </p>
        )}
        <div className='overflow-auto'>
          <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[100px]' />
              <col className='w-[120px]' />
              <col className='w-[120px]' />
              <col className='min-w-[125px]' />
              <col className='w-[120px]' />
              <col className='w-[108px]' />
              <col className='w-[124px]' />
              <col className='w-[180px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th
                  sortable
                  sortOrder={sortField === 'id' ? sortDirection : null}
                  onClick={() => handleSortChange('id')}
                >
                  診断ID
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'created_at' ? sortDirection : null}
                  onClick={() => handleSortChange('created_at')}
                >
                  開始日
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'diagnosis_at' ? sortDirection : null}
                  onClick={() => handleSortChange('diagnosis_at')}
                >
                  完了日
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'company_name_kana' ? sortDirection : null}
                  onClick={() => handleSortChange('company_name_kana')}
                >
                  企業名
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'companyUser.fullname_kana' ? sortDirection : null}
                  onClick={() => handleSortChange('companyUser.fullname_kana')}
                >
                  回答者
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'version' ? sortDirection : null}
                  onClick={() => handleSortChange('version')}
                >
                  診断ver
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'status' ? sortDirection : null}
                  onClick={() => handleSortChange('status')}
                >
                  ステータス
                </Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {diagnoses.length > 0
                ? diagnoses.map((diagnosis) => (
                    <tr
                      key={diagnosis.id}
                      className='[&:nth-child(2n)]:bg-gray-10'
                    >
                      <td>
                        <div className='px-4 py-6'>{diagnosis.id}</div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>{diagnosis.created_at}</div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>{diagnosis.diagnosis_on}</div>
                      </td>
                      <td>
                        <div className='truncate px-4 py-6'>{diagnosis.company.name}</div>
                      </td>
                      <td>
                        <div className='truncate px-4 py-6'>
                          {diagnosis.company_user.family_name} {diagnosis.company_user.first_name}
                        </div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>{diagnosis.version}</div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>
                          <span
                            className={
                              diagnosis.status === DxDiagnosisResultStatus.DELETED
                                ? 'text-warning-500'
                                : ''
                            }
                          >
                            {diagnosis.status_label}
                          </span>
                        </div>
                      </td>
                      <td>
                        {diagnosis.diagnosis_on && (
                          <div className='grid h-full place-items-center'>
                            <button
                              className='flex rounded border border-solid border-gray-200 bg-white px-6 py-[10px] text-sm'
                              onClick={() =>
                                navigate(`/tenant/dx-diagnosis/${diagnosis.id}/results`)
                              }
                            >
                              診断結果を見る
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
          {!searchedKeyword && pagination?.total === 0 ? (
            <p className='mt-4 text-sm'>データがありません。</p>
          ) : null}
        </div>
        {/* ページング */}
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </main>
  );
};
export default DxDiagnosisListForm;
